import { useAuth0, UserState } from "@bg/shared/lib/auth/react-auth0-wrapper";
import { usePermissions } from "@bg/shared/lib/hooks/usePermissions";
import { DefaultLayout, PrivateRoute } from "@bg/shared/lib/components";
import { fonts } from "@bg/shared/lib/theme";
import { deepSeaBlue, mediumGray } from "@bg/shared/lib/theme/colors";
import { EmailNotVerified, ErrorPage, NotFound } from "@bg/shared/lib/views";
import { CircularProgress, makeStyles } from "@material-ui/core";
import React, { FunctionComponent, lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Redirect,
  Route,
  RouteProps,
  Switch,
} from "react-router-dom";
import { PermissionType } from "@bg/shared/lib/dal/models";
import DashboardSaml from "./views/Dashboard/DashboardSaml";
import V2RedirectRoute from "@bg/shared/lib/components/V2RedirectRoute";
// import { copySessionStorageToCookies } from "@bg/shared/lib/utils/v2RedirectHelper";

interface PrivateAdminRouteProps extends Omit<RouteProps, "component"> {
  component: React.ComponentType<any>;
}

const PrivateAdminRoute: React.FC<PrivateAdminRouteProps> = ({
  component: Component,
  ...rest
}) => {
  const { isAdmin, isAuthenticated } = useAuth0();
  const enableUserAdminFlag = window.appConfig.features.enableUserAdmin;
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated && isAdmin && enableUserAdminFlag ? (
          <Component {...props} />
        ) : (
          <Redirect to="/dashboard" />
        )
      }
    />
  );
};

interface PrivatePermissionRouteProps extends Omit<RouteProps, "component"> {
  component: React.ComponentType<any>;
  permissions: PermissionType | PermissionType[];
}

const PrivatePermissionRoute: React.FC<PrivatePermissionRouteProps> = ({
  component: Component,
  permissions,
  ...rest
}) => {
  const { userManagementSupported, isAuthenticated, isAdmin } = useAuth0();
  const { hasPermission } = usePermissions();
  const enableUserAdminFlag = window.appConfig.features.enableUserAdmin;

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated &&
        userManagementSupported &&
        enableUserAdminFlag &&
        (hasPermission(permissions) || isAdmin) ? (
          <Component {...props} />
        ) : (
          <Redirect to="/dashboard" />
        )
      }
    />
  );
};

// Views
// import {  Registration, SignUp } from "./views";
const Dashboard = lazy(() => import("./views/Dashboard/Dashboard"));
const KitOrder = lazy(() => import("./views/KitOrder"));
const OrderRedirect = lazy(() => import("./views/Order/OrderRedirect"));
const OrderHistory = lazy(() => import("./views/OrderHistory"));
const Registration = lazy(() => import("./views/Registration"));
const TreatmentGuidancePage = lazy(() => import("./views/TreatmentGuidance"));
const KitHistory = lazy(() => import("./views/KitHistory"));
const SampleDetail = lazy(() => import("./views/SampleDetail"));
const OrderDetails = lazy(() => import("./views/OrderDetails"));
const ProfilePage = lazy(() => import("./views/ProfilePage"));
const OrderManifestUpload = lazy(
  () => import("./views/Order/OrderManifestUpload"),
);
const AppointmentForm = lazy(
  () => import("./views/Appointment/AppointmentForm"),
);

interface OrderDetailsProps extends RouteProps {
  isEnRoute: boolean;
}

const OrderDetailsRoute: FunctionComponent<OrderDetailsProps> = ({
  isEnRoute,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) => <OrderDetails {...props} isEnRoute={isEnRoute} />}
    />
  );
};

const UserMaintenance = lazy(() => import("./views/Users"));

const useStyles = makeStyles({
  email: {
    color: mediumGray,
    fontWeight: "normal",
    textDecoration: "none",
  },
  icon: {
    fontSize: "10em",
  },
  isLoading: {
    paddingTop: "25%",
    height: "100vh",
    textAlign: "center",
  },
  waitingForApproval: {
    paddingTop: "15%",
    height: "50vh",
    lineHeight: "1em",
    textAlign: "center",
    color: deepSeaBlue,
    fontWeight: 400,
    fontSize: fonts.sizes.sm,
    fontFamily: "Roboto Condensed",
    "& span": {
      color: mediumGray,
    },
  },
  iFrame: {
    display: "none",
  },
});

const VA_EMAIL_DOMAIN = "va.gov";

/** True when the email the user logged in with belongs to the VA. */
const isVaEmail = (email?: string): boolean =>
  !!email && email.toLowerCase().includes(VA_EMAIL_DOMAIN);

const Routes: FunctionComponent = () => {
  const { userState, physician, isV2Ui, user } = useAuth0();
  const classes = useStyles();

  useEffect(() => {
    const postCrossDomainMessage = () => {
      const doc = document.getElementById(
        "ifr-for-cross-domain",
      ) as HTMLIFrameElement;
      const token = sessionStorage.getItem("authToken");
      if (doc) {
        const contentWindow = doc.contentWindow;
        if (contentWindow && token) {
          const decodedToken = JSON.parse(atob(token.split(".")[1]));
          const tokenExpirationDate = decodedToken.exp * 1000;
          localStorage.setItem(
            "tokenExpirationDate",
            tokenExpirationDate.toString(),
          );
          if (physician) {
            contentWindow.postMessage(
              tokenExpirationDate,
              window.appConfig.catalogUrl,
            );
          }
        }
      }
    };
    postCrossDomainMessage();
  }, [physician]);

  // // Added isOnlyForLoading variable to prevent API calls in Header during Suspense fallback rendering
  const DefaultLayoutLoading = (isOnlyForLoading?: boolean) => (
    <DefaultLayout isOnlyForLoading={isOnlyForLoading}>
      <div className={classes.isLoading}>
        Loading... <br />
        <br />
        <CircularProgress />
      </div>
    </DefaultLayout>
  );
  const renderByState = () => {
    switch (userState) {
      case UserState.Error:
        return (
          <DefaultLayout>
            <ErrorPage />
          </DefaultLayout>
        );
      case UserState.Loading:
        return DefaultLayoutLoading();
      case UserState.WaitingForApproval:
        return (
          <DefaultLayout>
            <div className={classes.waitingForApproval}>
              <h2>Thank you for signing up.</h2>
              <h2>Your account is waiting approval.</h2>
              <br />
              <span>
                Once your account is approved you will receive an email
                confirmation.
              </span>
              <br />
              <span>
                This can take 1-2 business days. Please contact{" "}
                <a
                  className={classes.email}
                  href={`mailto: ${window.appConfig.enquiryEmail}`}
                >
                  Baylor&nbsp;Genetics
                </a>{" "}
                for assistance.{" "}
              </span>
            </div>
          </DefaultLayout>
        );
      case UserState.NeedRegistration:
        // VA users (va.gov email) are not allowed to self-register here, so
        // show the error page instead of the registration form. isVA from
        // useAuth0 is derived from the physician record, which an unregistered
        // user does not have yet, so the login email is what we can check.
        if (isVaEmail(user && user.email)) {
          return <ErrorPage />;
        }
        return (
          <Switch>
            <PrivateRoute path="/register" component={Registration} />
            <Redirect to="/register" />
          </Switch>
        );
      default:
        return (
          <Switch>
            <PrivateRoute path="/kit-order" component={KitOrder} />
            <V2RedirectRoute
              path="/dashboard"
              component={Dashboard}
              v2Path="/dashboard"
              enableRedirect={isV2Ui}
            />
            {/* <Route path="/dashboard-saml" component={DashboardSaml} /> */}
            <PrivateRoute
              path="/order/details/:id"
              component={(props: any) => (
                <OrderDetailsRoute {...props} isEnRoute={false} />
              )}
            />
            <PrivateRoute
              path="/order/en-route/:id"
              component={(props: any) => (
                <OrderDetailsRoute {...props} isEnRoute={true} />
              )}
            />
            <PrivateRoute path="/order" component={OrderRedirect} />
            <PrivateRoute
              path="/create/manifest"
              component={OrderManifestUpload}
            />
            {/* <PrivateRoute path="/create" component={OrderRedirect} /> */}

            <V2RedirectRoute
              path="/create"
              component={OrderRedirect}
              v2Path="/orders/create"
              enableRedirect={isV2Ui}
            />

            <V2RedirectRoute
              path="/orders"
              component={OrderHistory}
              v2Path="/dashboard"
              enableRedirect={isV2Ui}
            />
            <PrivateRoute path="/kits" component={KitHistory} />
            <PrivateRoute path="/sample/:sampleId" component={SampleDetail} />
            <PrivateRoute path="/profile" component={ProfilePage} />
            <Route path="/gc-appointment-form" component={AppointmentForm} />
            <PrivatePermissionRoute
              path="/users"
              component={UserMaintenance}
              permissions={[PermissionType.CreateUser, PermissionType.ViewUser]}
            />
            <Route
              path="/signup"
              render={() => {
                window.location.replace(
                  window.appConfig.v2FEUrl + "/auth/signup",
                );
                return null;
              }}
            />
            <Route
              path="/treatment/:treatmentId"
              component={TreatmentGuidancePage}
            />
            <Route path="/email-not-verified" component={EmailNotVerified} />
            <Redirect exact from="/" to="/dashboard" />
            <Redirect exact from="/register" to="/dashboard" />
            <Route component={NotFound} />
          </Switch>
        );
    }
  };

  return (
    <BrowserRouter>
      <iframe
        src={`${window.appConfig.catalogUrl}`}
        className={classes.iFrame}
        id="ifr-for-cross-domain"
        title="ifr-for-cross-domain"
        // onLoad={() => postCrossDomainMessage()}
      />
      <Suspense fallback={DefaultLayoutLoading(true)}>
        {renderByState()}
      </Suspense>
    </BrowserRouter>
  );
};

export default Routes;
